import { useEffect, useRef, useState } from "react";

import { Box, Stack, Tab, Tabs } from "@mui/material";

import { RefreshPageButton } from "components/RefreshPageButton";
import { useAuthContext, useShipments } from "hooks";
import { useIsMobile } from "hooks/useIsMobile";
import { useShipmentFiltersContext } from "hooks/useShipmentFiltersContext";
import { useSupplierShipments } from "hooks/useSupplierShipments";
import { SHIPMENTS_PAGE_SIZE } from "utils";

import { DashboardItemLayout, PageLayout } from "../../../../layouts";
import { ShipmentListContainer } from "../ShipmentListContainer";
import { ShipmentTemplateAndCreateShipmentFormDashboardItem } from "../ShipmentTemplateAndCreateShipmentFormDashboardItem";
import { SupplierShipmentListContainer } from "../SupplierShipments/SupplierShipmentListContainer";

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shipment-tabpanel-${index}`}
      aria-labelledby={`shipment-tab-${index}`}
    >
      {value === index && children}
    </div>
  );
}

export function VerticalDashboard() {
  const { user } = useAuthContext();
  const [tabValue, setTabValue] = useState<number>(0);
  const [myShipmentsPage, setMyShipmentsPage] = useState<number>(1);
  const [supplierShipmentsPage, setSupplierShipmentsPage] = useState<number>(1);
  const [scrollToShipments, setScrollToShipments] = useState(false);
  const shipmentListRef = useRef<HTMLDivElement | null>(null);

  const {
    shipments,
    error: shipmentsError,
    isLoading: shipmentsLoading,
    count: shipmentsCount,
    refreshShipments,
  } = useShipments({ page: myShipmentsPage, pageSize: SHIPMENTS_PAGE_SIZE });

  const {
    supplierShipments,
    error: supplierShipmentsError,
    isLoading: supplierShipmentsLoading,
    count: supplierShipmentsCount,
    refreshSupplierShipments,
  } = useSupplierShipments({
    page: supplierShipmentsPage,
    pageSize: SHIPMENTS_PAGE_SIZE,
  });

  const {
    registerRefreshCallback: registerRegularCallback,
    unregisterRefreshCallback: unregisterRegularCallback,
    filters: regularFilters,
  } = useShipmentFiltersContext("regular");
  const {
    registerRefreshCallback: registerSupplierCallback,
    unregisterRefreshCallback: unregisterSupplierCallback,
    filters: supplierFilters,
  } = useShipmentFiltersContext("supplier");

  const reduceDashboardItemSpacing = useIsMobile({ maxWidth: 1300 });
  const useColumnLayout = useIsMobile({ maxWidth: 1300 });

  useEffect(() => {
    registerRegularCallback(refreshShipments);
    return () => unregisterRegularCallback();
  }, [registerRegularCallback, unregisterRegularCallback, refreshShipments]);

  useEffect(() => {
    registerSupplierCallback(refreshSupplierShipments);
    return () => unregisterSupplierCallback();
  }, [
    registerSupplierCallback,
    unregisterSupplierCallback,
    refreshSupplierShipments,
  ]);

  useEffect(() => {
    setMyShipmentsPage(1);
  }, [regularFilters]);

  useEffect(() => {
    setSupplierShipmentsPage(1);
  }, [supplierFilters]);

  useEffect(() => {
    if (!shipmentsLoading && !supplierShipmentsLoading && scrollToShipments) {
      shipmentListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setScrollToShipments(false);
    }
  }, [shipmentsLoading, supplierShipmentsLoading, scrollToShipments]);

  if (!user) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const refreshCurrentTab = () => {
    if (tabValue === 0) {
      refreshShipments();
    } else {
      refreshSupplierShipments();
    }
  };

  return (
    <PageLayout>
      <Stack
        direction={useColumnLayout ? "column" : "row"}
        spacing={reduceDashboardItemSpacing ? 4 : 8}
      >
        <DashboardItemLayout
          sx={{
            p: "1rem",
          }}
        >
          <ShipmentTemplateAndCreateShipmentFormDashboardItem
            refreshShipments={refreshCurrentTab}
            triggerScrollToShipments={() => setScrollToShipments(true)}
          />
        </DashboardItemLayout>
        <DashboardItemLayout
          ref={shipmentListRef}
          sx={{
            p: "1rem",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="shipment tabs"
            >
              <Tab
                label={`My Shipments (${shipmentsCount})`}
                id="shipment-tab-0"
                aria-controls="shipment-tabpanel-0"
              />
              <Tab
                label={`Supplier Shipments (${supplierShipmentsCount})`}
                id="shipment-tab-1"
                aria-controls="shipment-tabpanel-1"
              />
            </Tabs>
            <RefreshPageButton
              id="dashboard-shipments-refresh"
              onRefresh={refreshCurrentTab}
              iconButton
            />
          </Box>
          <TabPanel value={tabValue} index={0}>
            <ShipmentListContainer
              page={myShipmentsPage}
              setPage={setMyShipmentsPage}
              shipments={shipments}
              shipmentsError={shipmentsError}
              shipmentsLoading={shipmentsLoading}
              shipmentCount={shipmentsCount}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <SupplierShipmentListContainer
              page={supplierShipmentsPage}
              setPage={setSupplierShipmentsPage}
              supplierShipments={supplierShipments}
              supplierShipmentsError={supplierShipmentsError}
              supplierShipmentsLoading={supplierShipmentsLoading}
              supplierShipmentCount={supplierShipmentsCount}
            />
          </TabPanel>
        </DashboardItemLayout>
      </Stack>
    </PageLayout>
  );
}
